import * as _ from 'lodash'
import React, { useEffect, useState } from 'react';
import valueFilters from '../valueFilters';
import util from '../util';
import { getInstallSheets, jobCostSchemaVersionCheck } from '../schemaHelper';
import keyboardUtil from '../keyboardUtils';
import enums from '../enum';
import componentServices from '../componentServices';
import * as actions from '../actions';
import globalRelay from '../globalRelay';
import installSheetFactory, { IInstallSheet } from '../models/installSheetModel';
import jobCostNoteFactory from '../models/jobCostNoteModel';
import { Confirm, CurrencyInput, Header, JobCostLockable, PercentInput, Popup, Radio } from './sharedComponents';
import { getModel, ModelCollection } from 'Scripts/store';
import { IJobCostChildProps } from './jobCostEdit';
import * as extensions from './extensions';

const KEY_DOWN_CODES = [13];

function InstallSetupSection({ className, jobCost, updateModel }: { className: string } & Pick<IJobCostChildProps, 'jobCost' | 'updateModel'>) {
	const currentChangeOrderKey = jobCost.currentChangeOrder && jobCost.currentChangeOrder.key;

	const createPendingInstallSheet = () => {
		return installSheetFactory({ key: util.generateGuid(), changeOrderKey: currentChangeOrderKey });
	}
	const [addClicked, setAddClicked] = useState(false);
	const [pendingInstallSheet, setPendingInstallSheet] = useState<IInstallSheet>(createPendingInstallSheet());
	const currentChangeOrder = jobCost.currentChangeOrder;
	const latestInstallConfigName = actions.formatConfigName({ companyVariant: jobCost.companyVariant, jobKey: jobCost.jobKey });
	const canUpdateConfig = !currentChangeOrder && jobCost.allowedContractEdits !== 'none' && jobCost.getSettingValue(enums.jobCostSettingGroups.General, 'installationConfigDefinition', undefined) !== latestInstallConfigName;

	const sheetRows = jobCost.allowInstallSheetChanges ? _.map(_.sortBy(getInstallSheets(jobCost), ['sortIndex']), sheet => {
		return (<tr className="added populated" key={sheet.key}>
			<td />
			<td><span className="readOnly"><strong>{sheet.sortIndex + 1}</strong></span></td>
			<td className={validate(sheet, 'name') ? '' : 'required'}>
				<input type="text" placeholder="Enter" autoCapitalize="" autoCorrect="off" value={sheet.viewValue('name')} onChange={e => updateSheet(sheet.key, 'name', e)} onBlur={e => updateSheet(sheet.key, 'name', e)} />
			</td>
			<td><button className="icon iconOnly contextMenu" onClick={e => handleContextMenuClicked(sheet.key, e)}><span /></button></td>
			<td />
		</tr>);
	}) : null;

	function handleAddClick() {
		const valid = validate(pendingInstallSheet);

		if (!valid) {
			setAddClicked(true);
		}
		else {
			const toAdd = pendingInstallSheet;
			toAdd.sortIndex = _.reduce(getInstallSheets(jobCost), (max, t) => t.sortIndex >= max ? t.sortIndex + 1 : max, 0);
			setPendingInstallSheet(createPendingInstallSheet());
			updateModel(model => {
				model.installSheets.add(toAdd);
				sortSheets(model);
			}, { autoSave: true });

			setAddClicked(false);		
		}
	}

	function validate (sheet, property?) {
		if (!property || sheet !== pendingInstallSheet || addClicked) {
			const result = !sheet.test(property);

			return result;
		}
		return true;
	}

	function handleContextMenuClicked (key, e) {
		const sheet = getInstallSheets(jobCost)[key];
		const hasSheetItems = _.some(jobCost.lineItems, { installSheetKey: key });

		const doDelete = () => {
			updateModel(model => {
				model.installSheets.delete(key);
				sortSheets(model);
			}, { autoSave: true });

			setTimeout(actions.popModal, 0);
		};

		const confirmDelete = () => {
			const confirm = <Confirm title={ `Permanently Delete ${sheet.name}?` } message={ `${sheet.name} has values that will be deleted with it. Are you sure you want to permanently delete it? This cannot be undone.` } confirmLabel="Permanently Delete" cancelLabel="Cancel" cancelHandler={ actions.popModal } confirmHandler={ doDelete } />;

			actions.popModal();
			actions.pushModal({ component: confirm });
		};

		const popup = (<Popup target={e.target}><div className="content scrollable">
			<ul className="menu">
				<li><a className="button icon iconLabel removeItem delete" onClick={ hasSheetItems ? confirmDelete : doDelete }><span>Delete</span></a></li>
			</ul>
		</div></Popup>);

		actions.pushModal({ component: popup });
	}

	function sortSheets (jobCost) {
		let sortIndex = 0;
		_.forEach(_.sortBy(_.filter(getInstallSheets(jobCost), { changeOrderKey: currentChangeOrderKey }), ['sortIndex']), sheet => {
			sheet.sortIndex = sortIndex++;
		});
	}

	function updateSheet(key, property, e) {
		const autoSave = e.type === 'blur';
		const newVal = (autoSave) ? valueFilters.clean(e.target.value) : e.target.value;

		updateModel(() => {
			const installSheet = _.find(getInstallSheets(jobCost), { key: key }) || pendingInstallSheet;

			installSheet.setAt(property, newVal);
		}, { autoSave: autoSave, updatePath: `installSheets/${key}/${property}` });
	}

	function handleUpdateInstallConfigClick() {
		updateModel(model => {
			// this allows for a forced update of the installation config definition until Legacy fully rolls out the new configuration. Once that is done this can be removed and simply trigger jobCostActions.JOB_COST_UPDATE
			model.setSetting(enums.jobCostSettingGroups.General, 'installationConfigDefinition', undefined, actions.formatConfigName({ companyVariant: model.companyVariant, jobKey: model.jobKey }));
			// this just ensures that globalRelay.triggerJobCostUpdate is called and that the view rerenders
		}, { autoSave: true, updatePath: '' });
	}

	if (jobCost.allowInstallSheetChanges) {
		sheetRows.unshift(<tr className="addRow" key={pendingInstallSheet.key}>
			<td />
			<td><span className="readOnly"><strong>{ }</strong></span></td>
			<td className={validate(pendingInstallSheet, 'name') ? '' : 'required'}>
				<input type="text" placeholder="Enter" autoCapitalize="" autoCorrect="off"
					value={pendingInstallSheet.viewValue('name')} onChange={e => updateSheet(pendingInstallSheet.key, 'name', e)}
					onBlur={e => updateSheet(pendingInstallSheet.key, 'name', e)} onKeyDown={e => keyboardUtil.onKeyDownCallback(e, KEY_DOWN_CODES, handleAddClick)} />
			</td>
			<td><button className="icon iconOnly addItem" onClick={handleAddClick}><span /></button></td>
			<td />
		</tr>);
	}
	return (<section>
		{!currentChangeOrder || jobCost.allowInstallSheetChanges ? <header><span>Installation &amp; Service Setup<span className="options" /></span>
			{!currentChangeOrder ? <div className="actions">
				<button className={`${canUpdateConfig ? '' : 'disabled'}`} onClick={canUpdateConfig ? handleUpdateInstallConfigClick : undefined}><span>Update Installation Config</span></button>
			</div> : null}
		</header> : null}
		<div className={className}>
			<table className="compact prepopulated" cellPadding="0" cellSpacing="0" style={{ marginTop: '20px' }}>
				<thead>
					{jobCost.allowInstallSheetChanges ? <tr>
						<td />
						<td><span className="readOnly">Install Number</span></td>
						<td><span className="readOnly">Description</span></td>
						<td className="action" />
						<td />
					</tr> : null}
				</thead>
				<tbody>
					{sheetRows}
				</tbody>
			</table>
		</div>
	</section>);

}

const JobCostSetupRows = (extensions as any).JobCostSetupRows || function ({ jobCost, updateModel }: Pick<IJobCostChildProps, 'jobCost' | 'updateModel'>) {
	const currentEmployee = componentServices.securityContext.getCurrentEmployee();
	const variantValue = jobCost.companyVariant || (currentEmployee && currentEmployee.getProfileValue('defaultCompanyVariant'));
	const globalConfig = globalRelay.config!.withVariant(variantValue);
	const setupConfig = globalConfig.jobCostSettings.setup;
	const rows = [];
	const [settingConfigItems, setSettingConfigItems] = useState({});
	useEffect(() => {
		if (setupConfig && setupConfig.editableSettings) {
			const editableSettings = globalRelay.filterState('editableSettings', { jobCost }, setupConfig.editableSettings);

			_.forEach(editableSettings, (settingConfig, key) => {
				switch (settingConfig.type) {
				case 'select': {
					const configItemsKey = key;
					if (settingConfig.dataAccessor) {
						globalRelay.executeGlobalFunction(settingConfig.dataAccessor, { jobCost: jobCost })
							.then(result => {
								setSettingConfigItems(previousState => {
									const newState = _.assign({}, previousState);

									newState[configItemsKey] = result;

									return newState;
								});
							});
					}

					break;
				}

				default:
				}
			});
		}
	}, [jobCost, setupConfig]);

	function updateSetting(name, autoSave, e) {
		// e.preventDefault();

		const newVal = (autoSave) ? valueFilters.clean(e.target.value) : e.target.value;

		updateModel((model, childUpdateCallback) => {
			const setting = model.setSetting(enums.jobCostSettingGroups.General, name, undefined, newVal);

			if (setting) {
				childUpdateCallback(setting);
			}
		}, { autoSave: autoSave, updatePath: 'settings' });
	}

	function handleUpdate(path, autoSave, e) {
		e.preventDefault();

		const newVal = (autoSave) ? valueFilters.clean(e.target.value) : e.target.value;

		updateModel(model => {
			const updateTarget = model.currentChangeOrder || model;

			updateTarget.setAt(path, newVal);

			// default the variant based on the employee profile value
			if (!model.companyVariant) {
				model.companyVariant = currentEmployee && currentEmployee.getProfileValue('defaultCompanyVariant');
			}
		}, { autoSave: autoSave, updatePath: path });
	}

	rows.push(<tr key="margin" className={!jobCost.margin ? 'required' : ''}>
		<td />
		<td><strong>Default Margin (%)</strong></td>
		<td><PercentInput placeholder="" autoCapitalize="" autoCorrect="off" value={jobCost.viewValue('margin')} onChange={e => handleUpdate('margin', false, e)} onBlur={e => handleUpdate('margin', true, e)} /></td>
		<td />
	</tr>);

	if (setupConfig && setupConfig.editableSettings) {
		const editableSettings = globalRelay.filterState('editableSettings', { jobCost }, setupConfig.editableSettings);

		_.forEach(editableSettings, (settingConfig, key) => {
			const value = jobCost.getSettingValue(enums.jobCostSettingGroups.General, key, undefined, settingConfig.defaultValue || '');
			let input;
			let items;

			switch (settingConfig.type) {
			case 'percent':
				input = <PercentInput placeholder="" autoCapitalize="" autoCorrect="off" value={value} onChange={e => updateSetting(key, false, e)} onBlur={e => updateSetting(key, true, e)} />;
				break;
			case 'select':
				items = settingConfig.items || settingConfigItems[key];

				if (items) {
					if (value && !_.find(items, { value: value })) {
						items.push({ value: value });
					}

					const options = _.map(items, item => <option key={item.value} value={item.value}>{item.label || item.value}</option>);

					input = <select className="select" placeholder="Select" value={value} onChange={e => updateSetting(key, true, e)}>{options}</select>;
				}
				break;
			case 'boolean':
				input = (<Radio buttons={[
					{ classNames: ['icon yes'], isSelected: (value === '1'), value: '1' },
					{ classNames: ['icon no'], isSelected: (value === '0'), value: '0' }
				]} onChange={(newButton) => updateSetting(key, true, { target: newButton })} />);
				break;
			default:
				input = <input type="text" placeholder="" autoCapitalize="" autoCorrect="off" value={value} onChange={e => updateSetting(key, false, e)} onBlur={e => updateSetting(key, true, e)} />;
			}

			rows.push(<tr key={ `setting-${key}`} className={ (settingConfig.required && !value) ? 'required' : ''}>
				<td />
				<td><strong>{settingConfig.label}</strong></td>
				<td>{input}</td>
				<td />
			</tr>);
		});
	}

	return <React.Fragment>{rows}</React.Fragment>
}

const ChangeOrderSetupRows = function({jobCost, typeName, updateModel}:  Pick<IJobCostChildProps, 'jobCost' | 'typeName' | 'updateModel'>) {
	const currentEmployee = componentServices.securityContext.getCurrentEmployee();
	const { store } = componentServices;
	const job = getModel({ store, collection: ModelCollection.jobs, key: jobCost.jobKey });
	const currentChangeOrder = jobCost.currentChangeOrder;
	const rows = [];

	function handleStatusChange(autoSave, e) {
		const newVal = (autoSave) ? valueFilters.clean(e.target.value) : e.target.value;

		updateModel(model => {
			const updateTarget = model.currentChangeOrder || model;

			if (newVal !== updateTarget.currentStatus) {
				updateTarget.statusLog.add({ status: newVal });
			}
		}, { autoSave: autoSave });
	}

	function updateNote(noteType, autoSave, e) {
		const newVal = autoSave ? valueFilters.trim(e.target.value) : e.target.value;

		updateModel(jobCost => {
			let note = jobCost.getNote(noteType);

			if (!note) {
				note = jobCostNoteFactory({ type: noteType });
				jobCost.notes.add(note);
			}

			note.content = newVal;
		}, { autoSave: autoSave });
	}

	function handleUpdate(path, autoSave, e) {
		
		e.preventDefault();

		const newVal = (autoSave) ? valueFilters.clean(e.target.value) : e.target.value;

		updateModel(model => {
			const updateTarget = model.currentChangeOrder || model;

			updateTarget.setAt(path, newVal);

			// default the variant based on the employee profile value
			if (!model.companyVariant) {
				model.companyVariant = currentEmployee && currentEmployee.getProfileValue('defaultCompanyVariant');
			}
		}, { autoSave: autoSave, updatePath: path });
	}

	rows.push(<tr key="reason" className={!currentChangeOrder.description ? 'required' : ''}>
		<td />
		<td><strong>Reason</strong></td>
		<td><input type="text" className="" placeholder={`Enter a Reason for this ${typeName}`} autoCapitalize="" autoCorrect="off" value={currentChangeOrder.viewValue('description')} onChange={e => handleUpdate('description', false, e)} onBlur={e => handleUpdate('description', true, e)} /></td>
		<td />
	</tr>);

	rows.push(<tr key="transactionAccount" className={!currentChangeOrder.transactionAccount ? 'required' : ''}>
		<td />
		<td><strong>Bill or Pay to</strong></td>
		<td><select className="select" placeholder="Select" value={currentChangeOrder.viewValue('transactionAccount')} onChange={e => handleUpdate('transactionAccount', true, e)} >
			<option>Select</option>
			<option>Homeowner</option>
			{job.builderKey ? <option>Builder</option> : null}
			<option>Job</option>
			<option>Service</option>
		</select>
		</td>
		<td />
	</tr>);

	if (currentChangeOrder.type === 'billing-adjustment') {
		rows.push(<tr key="amount" className={!currentChangeOrder.adjustedSellPrice ? 'required' : ''}>
			<td />
			<td><strong>Amount</strong></td>
			<td><CurrencyInput placeholder={`Enter an Amount for this ${typeName}`} value={currentChangeOrder.viewValue('adjustedSellPrice')} onChange={e => handleUpdate('adjustedSellPrice', false, e)} onBlur={e => handleUpdate('adjustedSellPrice', true, e)} /></td>
			<td />
		</tr>);
		rows.push(<tr key="clientNote">
			<td />
			<td><strong>Information for Client</strong></td>
			<td><textarea value={jobCost.getNoteContent(jobCostNoteFactory.NOTE_TYPES.CONTRACT_CUSTOMER_PRICING)} onChange={e => updateNote(jobCostNoteFactory.NOTE_TYPES.CONTRACT_CUSTOMER_PRICING, false, e)} onBlur={e => updateNote(jobCostNoteFactory.NOTE_TYPES.CONTRACT_CUSTOMER_PRICING, true, e)} placeholder="Enter notes for the Client" /></td>
			<td />
		</tr>);
	}
	rows.push(<tr key="status" className={!currentChangeOrder.description ? 'required' : ''}>
		<td />
		<td><strong>Status</strong></td>
		<td><select className="select" value={currentChangeOrder.isClosed ? "closed" : "unlocked"} onChange={e => handleStatusChange(true, e)} onBlur={e => handleStatusChange(true, e)}>
			<option value="unlocked">Open</option>
			<option value="closed">Closed</option>
		</select>
		</td>
		<td />
	</tr>);

	return <React.Fragment>{rows}</React.Fragment>
}

export default function JobCostEditSetup({ jobCost, typeName, updateModel }: Pick<IJobCostChildProps, 'jobCost' | 'typeName' | 'updateModel'>) {
	const currentEmployee = componentServices.securityContext.getCurrentEmployee();
	const currentChangeOrder = jobCost.currentChangeOrder;
	const updateTarget = currentChangeOrder || jobCost;
	const allowedChangeOrderInstallSheetSetup = jobCostSchemaVersionCheck(jobCost, 2) && jobCost.allowInstallSheetChanges;

	function handleUpdate(path, autoSave, e) {
		e.preventDefault();

		const newVal = (autoSave) ? valueFilters.clean(e.target.value) : e.target.value;

		updateModel(model => {
			const updateTarget = model.currentChangeOrder || model;

			updateTarget.setAt(path, newVal);

			// default the variant based on the employee profile value
			if (!model.companyVariant) {
				model.companyVariant = currentEmployee && currentEmployee.getProfileValue('defaultCompanyVariant');
			}
		}, { autoSave: autoSave, updatePath: path });
	}

	return (
		<section>
			<Header title="Setup" />
			<JobCostLockable jobCost={jobCost} isLockedOverride={((currentChangeOrder && currentChangeOrder.currentStatus === 'closed') ? false : undefined)}>{({ getClassName }) => {
				return (<React.Fragment>
					<div className={getClassName(['inner', 'compact', 'jobCostSetup'])}>

						<table className="" cellPadding="0" cellSpacing="0">
							<tbody>
								<tr className={!updateTarget.name ? 'required' : ''}>
									<td />
									<td><strong>Name</strong></td>
									<td><input type="text" className="" placeholder={`Enter a Name for this ${typeName}`} autoCapitalize="" autoCorrect="off" value={updateTarget.viewValue('name')} onChange={e => handleUpdate('name', false, e)} onBlur={e => handleUpdate('name', true, e)} /></td>
									<td />
								</tr>
								{!currentChangeOrder ? <JobCostSetupRows jobCost={jobCost} updateModel={updateModel} /> : <ChangeOrderSetupRows jobCost={jobCost} typeName={typeName} updateModel={updateModel} />}
							</tbody>
						</table>

					</div>

					{!currentChangeOrder || allowedChangeOrderInstallSheetSetup ? <InstallSetupSection className={getClassName(['inner', 'compact', 'jobCostSetup', ((currentChangeOrder && currentChangeOrder.currentStatus === 'closed') && 'disabled')])} jobCost={jobCost} updateModel={updateModel} /> : null}
				</React.Fragment>);
			}}
			</JobCostLockable>
		</section>

	);

}